/**
 * Búsqueda y análisis valorizado.
 *
 * Responde las preguntas que hoy obligan a filtrar la planilla a mano:
 * cuánto se despachó de un tipo de hormigón, cuánto compró un cliente, qué
 * consumió un centro de costo — siempre con la cantidad, el precio, los
 * recargos y el total en UF y en pesos.
 *
 * Los recargos incluyen el factor Codelco de sobrestadía, que es donde el
 * Excel dejaba plata sin cobrar.
 */

import type { RecargoCalculado, ReglaRecargo, UnidadItem } from './tipos';
import { roundTo, ufAClp } from './precios';

// ── Entrada ───────────────────────────────────────────────────────────────

/** Lo mínimo que el análisis necesita de una guía, ya valorizada. */
export interface GuiaValorizada {
  id: string;
  folio: number;
  fecha: string; // yyyy-MM-dd
  plantaCodigo: string;
  sector: string;
  turno: string | null;
  estado: string;

  clienteNombre: string | null;
  centroCostoCodigo: string | null;
  destinoTexto: string | null;
  proyectoNombre: string | null;
  equipoCodigo: string | null;
  operadorMixerNombre: string | null;

  productoCodigo: string | null;
  grado: string | null;
  mpa: number | null;
  cono: number | null;
  extras: string[];

  itemCodigo: string | null;
  itemDescripcion: string | null;
  precioUf: number;

  cantidadM3: number;
  montoPrincipalUf: number;
  recargos: RecargoCalculado[];
  montoRecargosUf: number;
  totalUf: number;

  minEstadiaObra: number | null;
  bloquesCodelco: number | null;
  tiemposCompletos: boolean;
}

// ── Filtros ───────────────────────────────────────────────────────────────

export interface FiltroAnalisis {
  /** Busca en folio, cliente, producto, destino, centro de costo, equipo y operador. */
  texto?: string;
  desde?: string;
  hasta?: string;
  plantas?: string[];
  clientes?: string[];
  centrosCosto?: string[];
  productos?: string[];
  destinos?: string[];
  grados?: string[];
  /** Resistencias exactas a incluir. Ej.: [30] trae todos los G30. */
  mpa?: number[];
  mpaMin?: number;
  mpaMax?: number;
  /** Extras que debe tener el producto: AIR, R3, R7, IMP, FP6… */
  extras?: string[];
  turnos?: string[];
  itemsContrato?: string[];
  incluirAnuladas?: boolean;
  soloConSobrestadia?: boolean;
  soloIncompletas?: boolean;
}

function normalizar(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

const enLista = (valor: string | null, lista?: string[]) =>
  !lista || lista.length === 0 || (valor !== null && lista.includes(valor));

export function filtrar(guias: GuiaValorizada[], f: FiltroAnalisis): GuiaValorizada[] {
  const texto = f.texto ? normalizar(f.texto.trim()) : '';
  const palabras = texto ? texto.split(/\s+/) : [];

  return guias.filter((g) => {
    if (!f.incluirAnuladas && g.estado === 'ANULADA') return false;
    if (f.desde && g.fecha < f.desde) return false;
    if (f.hasta && g.fecha > f.hasta) return false;

    if (!enLista(g.plantaCodigo, f.plantas)) return false;
    if (!enLista(g.clienteNombre, f.clientes)) return false;
    if (!enLista(g.centroCostoCodigo, f.centrosCosto)) return false;
    if (!enLista(g.productoCodigo, f.productos)) return false;
    if (!enLista(g.destinoTexto, f.destinos)) return false;
    if (!enLista(g.grado, f.grados)) return false;
    if (!enLista(g.turno, f.turnos)) return false;
    if (!enLista(g.itemCodigo, f.itemsContrato)) return false;

    if (f.mpa?.length && (g.mpa === null || !f.mpa.includes(g.mpa))) return false;
    if (f.mpaMin !== undefined && (g.mpa === null || g.mpa < f.mpaMin)) return false;
    if (f.mpaMax !== undefined && (g.mpa === null || g.mpa > f.mpaMax)) return false;

    if (f.extras?.length && !f.extras.every((x) => g.extras.includes(x))) return false;

    if (f.soloConSobrestadia && !(g.bloquesCodelco && g.bloquesCodelco > 0)) return false;
    if (f.soloIncompletas && g.tiemposCompletos) return false;

    if (palabras.length) {
      const campos = normalizar(
        [
          g.folio,
          g.clienteNombre,
          g.productoCodigo,
          g.destinoTexto,
          g.proyectoNombre,
          g.centroCostoCodigo,
          g.equipoCodigo,
          g.operadorMixerNombre,
          g.itemCodigo,
          g.itemDescripcion,
        ]
          .filter(Boolean)
          .join(' '),
      );
      // Todas las palabras deben aparecer: «promet g30» acota, no amplía.
      if (!palabras.every((p) => campos.includes(p))) return false;
    }

    return true;
  });
}

// ── Agrupación ────────────────────────────────────────────────────────────

export type Dimension =
  | 'item'
  | 'producto'
  | 'cliente'
  | 'centroCosto'
  | 'planta'
  | 'destino'
  | 'proyecto'
  | 'grado'
  | 'mpa'
  | 'turno'
  | 'equipo'
  | 'operador'
  | 'mes'
  | 'dia';

const ETIQUETA: Record<Dimension, string> = {
  item: 'Partida del contrato',
  producto: 'Producto',
  cliente: 'Cliente',
  centroCosto: 'Centro de costo',
  planta: 'Planta',
  destino: 'Destino',
  proyecto: 'Proyecto',
  grado: 'Grado',
  mpa: 'Resistencia',
  turno: 'Turno',
  equipo: 'Equipo',
  operador: 'Operador',
  mes: 'Mes',
  dia: 'Día',
};

export const DIMENSIONES: { id: Dimension; texto: string }[] = (
  Object.keys(ETIQUETA) as Dimension[]
).map((id) => ({ id, texto: ETIQUETA[id] }));

function claveDe(g: GuiaValorizada, d: Dimension): { clave: string; detalle: string | null } {
  switch (d) {
    case 'item':
      return { clave: g.itemCodigo ?? '(sin partida)', detalle: g.itemDescripcion };
    case 'producto':
      return { clave: g.productoCodigo ?? '(sin producto)', detalle: null };
    case 'cliente':
      return { clave: g.clienteNombre ?? '(sin cliente)', detalle: null };
    case 'centroCosto':
      return { clave: g.centroCostoCodigo ?? '(sin centro de costo)', detalle: null };
    case 'planta':
      return { clave: g.plantaCodigo, detalle: g.sector };
    case 'destino':
      return { clave: g.destinoTexto ?? '(sin destino)', detalle: null };
    case 'proyecto':
      return { clave: g.proyectoNombre ?? '(sin proyecto)', detalle: null };
    case 'grado':
      return { clave: g.grado ?? '(sin grado)', detalle: null };
    case 'mpa':
      return { clave: g.mpa !== null ? `G${g.mpa}` : '(sin resistencia)', detalle: null };
    case 'turno':
      return { clave: g.turno ?? '(sin turno)', detalle: null };
    case 'equipo':
      return { clave: g.equipoCodigo ?? '(sin equipo)', detalle: null };
    case 'operador':
      return { clave: g.operadorMixerNombre ?? '(sin operador)', detalle: null };
    case 'mes':
      return { clave: g.fecha.slice(0, 7), detalle: null };
    case 'dia':
      return { clave: g.fecha, detalle: null };
  }
}

export interface DesgloseRecargo {
  regla: ReglaRecargo;
  itemCodigo: string;
  cantidad: number;
  unidad: UnidadItem;
  montoUf: number;
}

export interface FilaAgrupada {
  clave: string;
  detalle: string | null;
  viajes: number;
  cantidadM3: number;
  /** Promedio ponderado del precio unitario, para lectura. */
  precioUfPromedio: number;
  montoPrincipalUf: number;
  montoRecargosUf: number;
  totalUf: number;
  totalClp: number;
  bloquesCodelco: number;
  recargos: DesgloseRecargo[];
  incompletas: number;
}

export interface ResultadoAnalisis {
  filas: FilaAgrupada[];
  totales: FilaAgrupada;
  guias: GuiaValorizada[];
}

/** Agrupa las guías filtradas por la dimensión elegida y valoriza cada grupo. */
export function agrupar(
  guias: GuiaValorizada[],
  dimension: Dimension,
  valorUf: number,
): ResultadoAnalisis {
  const grupos = new Map<string, GuiaValorizada[]>();
  const detalles = new Map<string, string | null>();

  for (const g of guias) {
    const { clave, detalle } = claveDe(g, dimension);
    grupos.set(clave, [...(grupos.get(clave) ?? []), g]);
    if (!detalles.has(clave)) detalles.set(clave, detalle);
  }

  const armar = (clave: string, detalle: string | null, xs: GuiaValorizada[]): FilaAgrupada => {
    const cantidadM3 = xs.reduce((s, g) => s + g.cantidadM3, 0);
    const montoPrincipalUf = xs.reduce((s, g) => s + g.montoPrincipalUf, 0);
    const montoRecargosUf = xs.reduce((s, g) => s + g.montoRecargosUf, 0);
    const totalUf = roundTo(montoPrincipalUf + montoRecargosUf, 4);

    // Desglose de recargos, sumado por regla.
    const porRegla = new Map<ReglaRecargo, DesgloseRecargo>();
    for (const g of xs) {
      for (const r of g.recargos) {
        const actual = porRegla.get(r.regla);
        porRegla.set(r.regla, {
          regla: r.regla,
          itemCodigo: r.itemCodigo,
          unidad: r.unidad,
          cantidad: roundTo((actual?.cantidad ?? 0) + r.cantidad, 3),
          montoUf: roundTo((actual?.montoUf ?? 0) + r.montoUf, 4),
        });
      }
    }

    return {
      clave,
      detalle,
      viajes: xs.length,
      cantidadM3: roundTo(cantidadM3, 3),
      precioUfPromedio: cantidadM3 > 0 ? roundTo(montoPrincipalUf / cantidadM3, 3) : 0,
      montoPrincipalUf: roundTo(montoPrincipalUf, 4),
      montoRecargosUf: roundTo(montoRecargosUf, 4),
      totalUf,
      totalClp: ufAClp(totalUf, valorUf),
      bloquesCodelco: xs.reduce((s, g) => s + (g.bloquesCodelco ?? 0), 0),
      recargos: [...porRegla.values()].sort((a, b) => b.montoUf - a.montoUf),
      incompletas: xs.filter((g) => !g.tiemposCompletos).length,
    };
  };

  const filas = [...grupos.entries()]
    .map(([clave, xs]) => armar(clave, detalles.get(clave) ?? null, xs))
    .sort((a, b) => b.totalUf - a.totalUf || b.cantidadM3 - a.cantidadM3);

  return {
    filas,
    totales: armar('TOTAL', null, guias),
    guias,
  };
}

// ── Opciones disponibles para los filtros ─────────────────────────────────

export interface OpcionesFiltro {
  plantas: string[];
  clientes: string[];
  centrosCosto: string[];
  productos: string[];
  destinos: string[];
  grados: string[];
  mpa: number[];
  extras: string[];
  turnos: string[];
}

/** Extrae los valores presentes en los datos, para no ofrecer filtros vacíos. */
export function opcionesDe(guias: GuiaValorizada[]): OpcionesFiltro {
  const set = <T>(f: (g: GuiaValorizada) => T | null | undefined) =>
    [...new Set(guias.map(f).filter((v): v is T => v !== null && v !== undefined && v !== ''))];

  const ordenarTexto = (xs: string[]) => xs.sort((a, b) => a.localeCompare(b, 'es'));

  return {
    plantas: ordenarTexto(set((g) => g.plantaCodigo)),
    clientes: ordenarTexto(set((g) => g.clienteNombre)),
    centrosCosto: ordenarTexto(set((g) => g.centroCostoCodigo)),
    productos: ordenarTexto(set((g) => g.productoCodigo)),
    destinos: ordenarTexto(set((g) => g.destinoTexto)),
    grados: ordenarTexto(set((g) => g.grado)),
    mpa: set((g) => g.mpa).sort((a, b) => a - b),
    extras: ordenarTexto([...new Set(guias.flatMap((g) => g.extras))]),
    turnos: ordenarTexto(set((g) => g.turno)),
  };
}

/** Nombre legible de cada regla de recargo, para las tablas. */
export const NOMBRE_RECARGO: Record<ReglaRecargo, string> = {
  CARGA_INCOMPLETA: 'Carga incompleta',
  SOBREESTADIA: 'Sobrestadía (factor Codelco)',
  BOMBEO_HN_A_HB: 'Bombeo HN a HB',
  AIRE_25_35: 'Aire incorporado G25-G35',
  AIRE_40_50: 'Aire incorporado G40-G50',
  DIF_CONO_HORMIGON: 'Diferencia de cono hormigón',
  DIF_CONO_SHOTCRETE: 'Diferencia de cono shotcrete',
  FIBRA_APORTE_XTREME: 'Fibra aporte Xtreme',
  RESISTENCIA_R3: 'Resistencia temprana R3',
  RESISTENCIA_R7: 'Resistencia temprana R7',
  CONFIANZA_95: 'Confianza 90% a 95%',
  IMPERMEABILIZANTE: 'Aditivo impermeabilizante',
  APERTURA_PLANTA: 'Apertura de planta',
};
